import { useEffect, useMemo, useState } from 'react'
import { useTextMeasurements } from './use-text-measurements'
import { Variant } from '@mui/material/styles/createTypography'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { BreadcrumbItem } from '../models'

interface Props {
  parentCategories: BreadcrumbItem[]
  iconButtonRef: React.RefObject<HTMLDivElement>
  wrapperRef: React.RefObject<HTMLDivElement>
  dotRef: React.RefObject<HTMLDivElement>
  variant: Variant
  breakLastBreadcrumb?: boolean
  contentSpacer?: number
}

export const useGroupedShortenedLabels = ({
  parentCategories,
  iconButtonRef,
  wrapperRef,
  dotRef,
  variant,
  breakLastBreadcrumb = false,
  contentSpacer = 0,
}: Props): { titles: BreadcrumbItem[]; menuItems: BreadcrumbItem[] } => {
  const [resizeTick, setResizeTick] = useState(0)
  const measureTextWidth = useTextMeasurements(variant)
  const { t } = useCustomTranslation()

  const titles = parentCategories.map(cat => {
    return { name: cat.id === 0 ? t('downloads.root-category-name') : cat.name, id: cat.id }
  })
  const iconButtonWidth = iconButtonRef.current ? iconButtonRef.current.clientWidth : 0
  const parentWidth = wrapperRef.current ? wrapperRef.current.clientWidth : 0
  const dotWidth = dotRef.current ? dotRef.current.clientWidth : 8
  const lastIdx = parentCategories.length - 1
  const availableWidth = parentWidth - iconButtonWidth
  const additionalSpacerCount = breakLastBreadcrumb ? 1 : 0

  let i = 0

  useEffect(() => {
    const handleResize = (): void => setResizeTick(tick => tick + 1)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return useMemo(() => {
    const newTitles = [...titles]

    let usedTextWidth = newTitles.reduce((a, b) => a + measureTextWidth(b.name) + contentSpacer, 0)

    const menuItems: BreadcrumbItem[] = []

    let usedSpacerWidth = (newTitles.length - 1 + additionalSpacerCount) * iconButtonWidth

    /* while combined width of text and spacers exceeds available width, group first breadcrumb into menu */

    while (usedTextWidth + usedSpacerWidth > availableWidth) {
      newTitles[0].name = '…'
      menuItems.push(parentCategories[i])

      if (i > 0) {
        newTitles.splice(1, 1)
      }

      const labelWidths = newTitles.map(title => measureTextWidth(title.name) + contentSpacer)

      usedTextWidth = newTitles.reduce((totalLength, currentTitle, idx) => {
        const width = currentTitle.name === '…' ? dotWidth : labelWidths[idx]
        return totalLength + width
      }, 0)

      usedSpacerWidth = (newTitles.length - 1 + additionalSpacerCount) * iconButtonWidth
      i++
      if (i > lastIdx || (!breakLastBreadcrumb && i === lastIdx)) break
    }

    return { titles: newTitles, menuItems }
  }, [JSON.stringify(titles), parentCategories.length, availableWidth, resizeTick, breakLastBreadcrumb]) // eslint-disable-line react-hooks/exhaustive-deps
}
